import { Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import {
  AccountColumnData,
  AccountUser,
  IAccountsDetailData,
  IAddressInfoDetailsData,
  IBusinessInfoDetailsData,
  ICustomerManagementDetailsData,
  IGovernmentInfoDetailsData,
} from 'src/app/features/model/sm-accounts';
import {
  AddAccountUser,
  FetchAccountDropdowns,
  FetchAccountList,
  FetchAccountsInformationListSuccess,
  FetchSelectedAccountById,
  RemoveAccountUser,
  ResetAccountList,
  ResetAccountsInformationList,
  SaveAccountsDetail,
  SaveAddressDetails,
  SaveBusinessDetails,
  SaveCustomerManagementDetails,
  SaveGovernmentDetails,
  SetAccountUsers,
  UpdateAccountUser,
} from './accounts.action';
import {
  defaultAccountsAddressInfoData,
  defaultAccountsBusinessInfoData,
  defaultAccountsDetailData,
  defaultAccountsGovernmentInfoData,
  defaultCustomerManagementData,
} from '../accounts.config';
import { updateState } from 'src/app/shared/utility/store.utility';
import { AccountDropdownList } from 'src/app/features/model/account.model';
import { patch } from '@ngxs/store/operators';

// Accounts Detail Section
@State<IAccountsDetailData>({
  name: 'accountsDetail',
  defaults: {
    ...defaultAccountsDetailData(),
  },
})
@Injectable()
export class AccountsDetailState {
  @Action(SaveAccountsDetail)
  saveAccountsDetailData(
    ctx: StateContext<IAccountsDetailData>,
    action: SaveAccountsDetail
  ) {
    updateState(ctx, action.payload);
  }
}

// Accounts Address Section
@State<IAddressInfoDetailsData>({
  name: 'accountsAddressInfo',
  defaults: {
    ...defaultAccountsAddressInfoData(),
  },
})
@Injectable()
export class AccountsAddressInfoState {
  @Action(SaveAddressDetails)
  saveAddressDetailsData(
    ctx: StateContext<IAddressInfoDetailsData>,
    action: SaveAddressDetails
  ) {
    updateState(ctx, action.payload);
  }
}

// Business Information Section
@State<IBusinessInfoDetailsData>({
  name: 'accountsBusinessInfo',
  defaults: {
    ...defaultAccountsBusinessInfoData(),
  },
})
@Injectable()
export class AccountsBusinessInfoState {
  @Action(SaveBusinessDetails)
  saveBusinessDetailsData(
    ctx: StateContext<IBusinessInfoDetailsData>,
    action: SaveBusinessDetails
  ) {
    updateState(ctx, action.payload);
  }
}

// Customer Management Information
@State<ICustomerManagementDetailsData>({
  name: 'accountsCustomerManagement',
  defaults: {
    ...defaultCustomerManagementData(),
  },
})
@Injectable()
export class AccountsCustomerManagementState {
  @Action(SaveCustomerManagementDetails)
  saveCustomerManagementDetailsData(
    ctx: StateContext<ICustomerManagementDetailsData>,
    action: SaveCustomerManagementDetails
  ) {
    updateState(ctx, action.payload);
  }
}

// Government Information
@State<IGovernmentInfoDetailsData>({
  name: 'accountsGovernmentInfo',
  defaults: {
    ...defaultAccountsGovernmentInfoData(),
  },
})
@Injectable()
export class AccountsGovernmentInfoState {
  @Action(SaveGovernmentDetails)
  saveGovernmentDetailsData(
    ctx: StateContext<IGovernmentInfoDetailsData>,
    action: SaveGovernmentDetails
  ) {
    updateState(ctx, action.payload);
  }
}

// Account List
export interface AccountListStateModel {
  account: AccountColumnData[];
  totalData: number;
}

@Injectable()
@State<AccountListStateModel>({
  name: 'account',
  defaults: {
    account: [],
    totalData: 0,
  },
})
export class AccountListState {
  @Action(FetchAccountList)
  FetchHawbsList(
    ctx: StateContext<AccountListStateModel>,
    action: FetchAccountList
  ) {
    const payload = action.payload;
    if (payload.data?.account && payload.data?.totalRecords) {
      updateState(ctx, {
        account: payload.data.account,
        totalData: payload.data.totalRecords,
      });
    } else {
      updateState(ctx, {
        account: [],
        totalData: 0,
      });
    }
  }

  @Action(ResetAccountList)
  ResetAccountList(ctx: StateContext<AccountListStateModel>) {
    updateState(ctx, {
      account: [],
      totalData: 0,
    });
  }
}

// Account CRUD
export interface AccountInformationStateModel {
  accountInformation: any;
  accountsInformationList: any[];
}

@State<AccountInformationStateModel>({
  name: 'accountInformation',
  defaults: {
    accountInformation: {},
    accountsInformationList: [],
  },
})
@Injectable()
export class AccountInformationState {
  @Action(FetchAccountsInformationListSuccess)
  fetchAccountsInformationListSuccess(
    { getState, setState }: StateContext<AccountInformationStateModel>,
    { accounts }: FetchAccountsInformationListSuccess
  ): void {
    const state = getState();
    setState({
      ...state,
      accountsInformationList: accounts,
    });
  }

  @Action(FetchSelectedAccountById)
  FetchSelectedAccountById(
    ctx: StateContext<AccountInformationStateModel>,
    action: FetchSelectedAccountById
  ) {
    const state = ctx.getState();
    const selectedAccount = state.accountsInformationList.find(
      element => element.accountId === action.accountId
    );

    updateState(ctx, {
      accountInformation: selectedAccount || {},
    });
  }

  @Action(ResetAccountsInformationList)
  resetAccountInfo(ctx: StateContext<AccountInformationStateModel>) {
    updateState(ctx, {
      accountInformation: {},
      accountsInformationList: [],
    });
  }
}

@Injectable()
@State<AccountDropdownList>({
  name: 'accountDropdowns',
  defaults: {
    organization: [],
    attachmentType: [],
    clearanceType: [],
    clearanceChannel: [],
    fullmanValidation: [],
    countries: [],
    deliveryTerms: [],
  },
})
export class AccountDropdownsListState {
  @Action(FetchAccountDropdowns)
  saveShipmentLookUpData(
    ctx: StateContext<AccountDropdownList>,
    action: FetchAccountDropdowns
  ) {
    const state = ctx.getState();
    ctx.setState({
      ...state,
      organization: action.payload.organization,
      attachmentType: action.payload.attachmentType,
      clearanceType: action.payload.clearanceType,
      clearanceChannel: action.payload.clearanceChannel,
      fullmanValidation: action.payload.fullmanValidation,
      countries: action.payload.countries,
      deliveryTerms: action.payload.deliveryTerms,
    });
  }
}

export interface AccountUsersStateModel {
  users: AccountUser[];
}

@State<AccountUsersStateModel>({
  name: 'accountUsers',
  defaults: {
    users: [],
  },
})
@Injectable()
export class AccountUserState {
  @Action(SetAccountUsers)
  setAccountUsers(
    ctx: StateContext<AccountUsersStateModel>,
    action: SetAccountUsers
  ) {
    ctx.setState({
      users: action.payload || [],
    });
  }

  @Action(AddAccountUser)
  addAccountUser(
    ctx: StateContext<AccountUsersStateModel>,
    action: AddAccountUser
  ) {
    ctx.setState({
      users: [action.payload, ...ctx.getState().users],
    });
  }

  @Action(UpdateAccountUser)
  updateAccountUser(
    ctx: StateContext<AccountUsersStateModel>,
    action: UpdateAccountUser
  ) {
    ctx.setState(
      patch({
        users: ctx.getState().users.map(user => {
          if (
            user.id === action.payload.id ||
            user.email === action.payload.email
          ) {
            return { ...user, ...action.payload };
          }

          return user;
        }),
      })
    );
  }

  @Action(RemoveAccountUser)
  removeAccountUser(
    ctx: StateContext<AccountUsersStateModel>,
    action: RemoveAccountUser
  ) {
    ctx.setState(
      patch({
        users: ctx
          .getState()
          .users.filter(
            user =>
              !(
                user.id === action.payload.id ||
                user.email === action.payload.email
              )
          ),
      })
    );
  }
}
